import { AnyAction } from 'redux';
import { CancelTokenSource } from 'axios';
import { ServerResponse } from 'data-layer-cr/dist/models';

// models
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import { QuotationData } from 'data-layer-cr/dist/responseObjects';
import EmissionDocument from 'src/models/emission/EmissionDocument';

// functions
import { isInspectionRequired } from 'data-layer-cr/dist/functions/quotations';
import {
  hasIdentityDocument,
  hasOptionalIdentityDocument,
} from 'src/functions/fileManagerMethods';

// Network
import processRequest from '../../../services/processRequest';

// Redux
import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

// Actions
import {
  updateCurrentFileDownload,
  updateDownloadStatus,
  updateFileData,
  updateErrorDetail,
  updateIsErrorDownload,
  updateMenuDownloadOptions,
  updateShowDownloadMenuStatus,
  updateMenuDownloadStarted,
} from '../slice/policySlice';

export const resetPolicyState =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateIsErrorDownload(false));
    dispatch(updateErrorDetail(''));
    dispatch(updateDownloadStatus('initial'));
    dispatch(updateCurrentFileDownload(''));
    dispatch(updateFileData({ content: '', mime: '', name: '' }));
  };

const fetchPolicyDocument = async (
  urlSalt: string,
  fileName: string,
  cancelTokenSource: CancelTokenSource,
): Promise<EmissionDocument> => {
  if (fileName === 'inspectionReport') {
    const { data } = await processRequest<ServerResponse<string>>({
      method: 'GET',
      url: `inspections/${urlSalt}/file/inspectionReport`,
      cancelTokenSource,
    });
    return {
      content: data,
      mime: 'application/pdf',
      name: 'InspectionReport.pdf',
    };
  }

  const { data } = await processRequest<ServerResponse<EmissionDocument>>({
    method: 'GET',
    url: `/PolicyEmission/documents/${urlSalt}?document=${fileName}`,
    cancelTokenSource,
  });

  return {
    content: `data:${data.mime};base64,${data.content}`,
    mime: data.mime,
    name: data.name,
  };
};

/**
 * Request the vehicle registry base64 file
 *
 * @param urlSalt - url salt identifying the quotation
 * @param fileName - The file name to look for
 * @param cancelTokenSource - axios cancel token ref, allows the ui to cancel the request without dispatching
 * another action
 */
export const downloadPolicyFiles =
  (
    urlSalt: string,
    fileName: string,
    cancelTokenSource: CancelTokenSource,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(updateDownloadStatus('requesting'));
    dispatch(updateCurrentFileDownload(fileName));
    try {
      const data = await fetchPolicyDocument(
        urlSalt,
        fileName,
        cancelTokenSource,
      );
      dispatch(updateIsErrorDownload(false));
      dispatch(updateFileData(data));
      return dispatch(updateDownloadStatus('success'));
    } catch (e) {
      dispatch(updateIsErrorDownload(true));
      dispatch(updateDownloadStatus('failure'));
      return dispatch(updateErrorDetail('failure'));
    }
  };

export const startDownloadFromMenu =
  (quotationHash: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(updateMenuDownloadStarted(true));
    dispatch(updateShowDownloadMenuStatus(true));

    try {
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `quotation?hash=${quotationHash}`,
      });
      const { quotation } = response.data;
      const { createdAt } = quotation;
      const inspectionRequired = isInspectionRequired(quotation, true);
      const existIdentityDocument = hasIdentityDocument(
        quotation.additionalDocuments,
      );
      const existOptionalIdentityDocument = hasOptionalIdentityDocument(
        quotation.additionalDocuments,
      );
      dispatch(
        updateMenuDownloadOptions({
          hasIdentityDocument: existIdentityDocument,
          hasOptionalIdentityDocument: existOptionalIdentityDocument,
          quotationHash,
          inspectionRequired,
          startDownload: true,
          createdAt,
        }),
      );
      return dispatch(updateMenuDownloadStarted(false));
    } catch (e) {
      dispatch(updateMenuDownloadStarted(false));
      return dispatch(updateShowDownloadMenuStatus(false));
    }
  };

export const restartDownloadFromMenu =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(
      updateMenuDownloadOptions({
        quotationHash: '',
        startDownload: false,
        inspectionRequired: false,
        createdAt: '',
        hasIdentityDocument: false,
        hasOptionalIdentityDocument: false,
      }),
    );
  };

export const unsetShowDownloadMenuStatus =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateShowDownloadMenuStatus(false));
  };
